import React, { useEffect, useMemo, useState } from 'react';
import Table from 'rc-table';
import type { ColumnType } from 'rc-table/lib/interface';
import 'react-toastify/dist/ReactToastify.css';
import { SypacAvatar, SypacText } from '@sypac/component-library-react';
import './contracts-table.css';
import { T, useTranslate } from '@tolgee/react';
import { formatDate, formatTimeSince } from '../../utils/time.util';
import Tons from '../../assets/Tons';
import { NumericFormat } from 'react-number-format';
import { Contract } from '../../services/contracts.service';
import ContractStatusBadge from './components/ContractStatusBadge';
import { Tooltip } from 'react-tooltip';
import { numberTwoDecimal, toNumber } from '../../utils/number.util';
import { cn } from '../../lib/utils';
import { OrderInterface } from '../OrdersTable/interfaces/Order.interface';
import { TruckDto } from '../../services/truck.services';
import EmptyPlaceholder from './components/EmptyPlaceholder';

interface ContractsTableProps {
	rows: Contract[];
	rowClick: (contract: Contract) => void;
	search?: string;
	clickedOutside?: boolean;
	role?: 'admin' | 'buyer' | 'producer';
	currentTab?: string;
	selectedContractId?: number | null;
	onCompanyClick?: (companyId: number) => void;
}

export const ContractsTable: React.FC<ContractsTableProps> = ({
	rows,
	rowClick,
	search,
	clickedOutside,
	role = 'admin',
	currentTab = 'all',
	selectedContractId,
	onCompanyClick,
}) => {
	const { t } = useTranslate();
	const language = localStorage.getItem('lang') || 'en';
	const [keyRows, setRows] = useState<Contract[]>([]);
	const [selectedRowKey, setSelectedRowKey] = useState<number | null>(null);

	useEffect(() => {
		if (selectedContractId != null) {
			setSelectedRowKey(selectedContractId);
		}
	}, [selectedContractId]);

	const columns = useMemo<ColumnType<Contract>[]>(() => {
		const idCol: ColumnType<Contract> = {
			title: (
				<SypacText variant="body-regular-medium">
					<p className="text-gray-40 text-[14px] leading-[20px]">
						<T keyName="contractsTable.contractID">Contract ID</T>
					</p>
				</SypacText>
			),
			dataIndex: '',
			className: 'text-left',
			key: 'id',
			width: 100,
			render(_value: any, record: Contract) {
				const htmlName = search
					? String(record.id)?.replace(
							new RegExp(search, 'gi'),
							(match: any) => `<span class="text-texas-rose">${match}</span>`,
					  )
					: String(record.id);

				return (
					<div className="flex flex-col items-start justify-center gap-[10px]">
						<div className="flex gap-2 items-center justify-center">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">ID:</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p
									className="text-gray-80"
									dangerouslySetInnerHTML={{ __html: htmlName }}
								/>
							</SypacText>
							<div className="w-[2px] h-[2px] bg-gray-60 rounded-full" />
							<span
								data-tooltip-id="order-hint"
								data-tooltip-content={t(
									'ordersTable.timeSinceContractCreated',
									'Time since Contract created',
								)}
							>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<T
											keyName={`baseOrderDetails.${formatTimeSince(
												record.createdAt,
												false,
											)}`}
										>
											{formatTimeSince(record.createdAt, false)}
										</T>
									</p>
								</SypacText>
							</span>
						</div>

						<ContractStatusBadge status={record.status} />
						<Tooltip
							place="top"
							id="order-hint"
							style={{
								backgroundColor: '#E8E8E8',
								color: '#000000',
							}}
						/>
					</div>
				);
			},
		};

		const buyerCol: ColumnType<Contract> = {
			title: (
				<div className="flex items-center gap-3">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-40 text-[14px] leading-[20px]">
							<T keyName="contractsTable.buyer">Buyer</T>
						</p>
					</SypacText>
				</div>
			),
			dataIndex: '',
			className: 'text-left',
			key: 'buyer',
			width: 100,
			render(_value: any, record: Contract) {
				return (
					<div className="h-full flex flex-col justify-center gap-[6px]">
						<div className="w-[279px] flex items-center gap-[10px]">
							<SypacAvatar size="sm" initials={record.buyer.name || 'F'} />
							<a
								className={cn(
									'truncate no-underline',
									role === 'admin'
										? 'text-cornflower-blue hover:underline cursor-pointer'
										: 'text-gray-80',
								)}
								href="/"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (role === 'admin' && onCompanyClick && record.buyer?.id) {
										onCompanyClick(record.buyer.id);
									}
								}}
							>
								{record.buyer.name || 'First Emporium & Supermarket Branch'}
							</a>
						</div>
						<div className="flex gap-[10px] items-center">
							<SypacAvatar
								size="sm"
								initials={
									`${record.buyer.contact?.firstName} ${record.buyer.contact?.lastName}` ||
									'DR'
								}
							/>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 mt-[2px]">
									{`${record.buyer.contact?.firstName} ${record.buyer.contact?.lastName}` ||
										'Dean Russel'}
								</p>
							</SypacText>
						</div>
					</div>
				);
			},
		};

		const sellerCol: ColumnType<Contract> = {
			title: (
				<div className="flex items-center gap-3">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-40 text-[14px] leading-[20px]">
							<T keyName="contractsTable.seller">Seller</T>
						</p>
					</SypacText>
				</div>
			),
			dataIndex: '',
			className: 'text-left',
			key: 'seller',
			width: 100,
			render(_value: any, record: Contract) {
				return (
					<div
						className="h-full flex flex-col justify-center gap-[6px]"
						onClick={() => rowClick(record)}
					>
						<div className="w-[279px] flex items-center gap-[10px]">
							<SypacAvatar size="sm" initials={record.seller.name || 'F'} />
							<a
								className={cn(
									'truncate no-underline',
									role === 'admin'
										? 'text-cornflower-blue hover:underline cursor-pointer'
										: 'text-gray-80',
								)}
								href="/"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									if (role === 'admin' && onCompanyClick && record.seller?.id) {
										onCompanyClick(record.seller.id);
									}
								}}
							>
								{record.seller.name || 'First Emporium & Supermarket Branch'}
							</a>
						</div>
						<div className="flex gap-[10px] items-center">
							<SypacAvatar
								size="sm"
								initials={
									`${record.seller.contact?.firstName} ${record.seller.contact?.lastName}` ||
									'DR'
								}
							/>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40 mt-[2px]">
									{`${record.seller.contact?.firstName} ${record.seller.contact?.lastName}` ||
										'Dean Russel'}
								</p>
							</SypacText>
						</div>
					</div>
				);
			},
		};

		const productCol: ColumnType<Contract> = {
			title: t('contractsTable.product', 'Product & quantity'),
			dataIndex: '',
			className: 'text-left',
			key: 'productQuantity',
			width: 50,
			render(_value: any, record: Contract) {
				const productName = `${
					record?.lines[0].product?.translations?.find(
						(locale) => locale.language === language,
					)?.name || record?.lines[0].product?.name
				}`;
				const productSize = `${
					record?.lines[0].product?.type
						? `${record?.lines[0].product?.type}`
						: ''
				} ${
					record?.lines[0].product?.size
						? `(${record?.lines[0].product?.size})`
						: ''
				}`;

				const htmlName = search
					? productName?.replace(
							new RegExp(search, 'gi'),
							(match: any) => `<span class="text-texas-rose">${match}</span>`,
					  )
					: productName;

				const htmlSize = search
					? productSize?.replace(
							new RegExp(search, 'gi'),
							(match) => `<span class="text-texas-rose">${match}</span>`,
					  )
					: productSize;

				const line = record?.lines?.[0];

				const contractQty = toNumber(line?.contractQtyTons);
				const rawOrders = (record as any)?.orders;
				const contractOrders: OrderInterface[] = Array.isArray(rawOrders)
					? rawOrders
					: Array.isArray(rawOrders?.items)
					? rawOrders.items
					: Array.isArray(rawOrders?.data)
					? rawOrders.data
					: [];

				const getExecutedQtyForOrder = (order: OrderInterface) => {
					const trucks = order?.trucks ?? [];

					if (trucks.length > 1) {
						return trucks.reduce(
							(sum: number, tr: TruckDto) =>
								sum + toNumber(tr?.loadedQuantity ?? tr?.payload),
							0,
						);
					}

					if (trucks.length === 1) {
						const tr = trucks[0];
						const hasLoaded =
							tr?.loadedQuantity !== undefined && tr?.loadedQuantity !== null;

						return hasLoaded
							? toNumber(tr.loadedQuantity)
							: toNumber(order?.quantity);
					}

					return toNumber(order?.quantity);
				};

				const executedQtyTotal = contractOrders.reduce(
					(sum: number, order: OrderInterface) =>
						sum + getExecutedQtyForOrder(order),
					0,
				);

				const remainingQtyRawTons = contractQty - executedQtyTotal;
				const remainingQtyTons = numberTwoDecimal(remainingQtyRawTons);

				return (
					<div className="flex gap-3 items-center">
						<div
							style={{
								backgroundImage: `url(${record?.lines[0].product.photoUrl})`,
							}}
							className="flex flex-none w-[58px] h-[58px] bg-no-repeat bg-cover rounded-10 border border-solid border-gray-10"
							role="img"
						/>
						<div className="flex flex-col gap-2 justify-center w-full mt-[1px]">
							<div className="flex items-center gap-[10px]">
								<SypacText variant="body-regular-medium">
									<p dangerouslySetInnerHTML={{ __html: htmlName }} />
								</SypacText>
								{productSize.trim() ? (
									<div className="w-[2px] h-[2px] bg-gray-40 rounded-full" />
								) : null}
								<SypacText variant="body-regular-medium">
									<p dangerouslySetInnerHTML={{ __html: htmlSize }} />
								</SypacText>
							</div>

							<div className="flex items-center gap-3">
								<div className="flex items-center gap-[10px]">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40 text-[14px] leading-[16px]">
											<T keyName="contractsTable.contractQuantity">
												Contract quantity:
											</T>
										</p>
									</SypacText>

									<div className="h-[16px] flex items-center gap-[10px]">
										<div>
											<Tons width={14} />
										</div>
										<SypacText variant="body-regular-medium">
											<p className="text-gray-40 text-[14px] leading-[16px]">
												<NumericFormat
													type="text"
													displayType="text"
													thousandSeparator=" "
													decimalSeparator="."
													value={record?.lines[0].contractQtyTons}
												/>{' '}
												<T keyName="basicDetails.tons">t</T>
											</p>
										</SypacText>
									</div>
								</div>

								{remainingQtyRawTons > 0 && (
									<>
										<SypacText variant="body-regular-medium">
											<span className="text-gray-10">|</span>
										</SypacText>
										<div className="flex items-center gap-[10px]">
											<SypacText variant="body-regular-medium">
												<p className="text-gray-40 text-[14px] leading-[16px]">
													<T keyName="contractsTable.remainingQuantity">
														Remaining quantity:
													</T>
												</p>
											</SypacText>

											<div className="h-[16px] flex items-center gap-[10px]">
												<div>
													<Tons width={14} />
												</div>
												<SypacText variant="body-regular-medium">
													<p className="text-gray-40 text-[14px] leading-[16px]">
														<NumericFormat
															type="text"
															displayType="text"
															thousandSeparator=" "
															decimalSeparator="."
															value={remainingQtyTons}
														/>{' '}
														<T keyName="basicDetails.tons">t</T>
													</p>
												</SypacText>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				);
			},
		};

		const orderedQuantity: ColumnType<Contract> = {
			title: t('contractsTable.orderedQuantity', 'Ordered quantity'),
			dataIndex: '',
			className: 'text-left',
			key: 'orderedQuantity',
			width: 50,
			render(_value: any, record: Contract) {
				return (
					<div className="flex gap-[10px]">
						{record?.lines[0].orderedQtyTons > 0 ? (
							<>
								<Tons />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={record?.lines[0].orderedQtyTons}
										/>{' '}
										<T keyName="basicDetails.tons">t</T>
									</p>
								</SypacText>
							</>
						) : (
							'—'
						)}
					</div>
				);
			},
		};

		const deliveredQuantity: ColumnType<Contract> = {
			title: t('contractsTable.deliveredQuantity', 'Delivered quantity'),
			dataIndex: '',
			className: 'text-left',
			key: 'deliveredQuantity',
			width: 50,
			render(_value: any, record: Contract) {
				return (
					<div className="flex gap-[10px]">
						{record?.lines[0].deliveredQtyTons > 0 ? (
							<>
								<Tons />
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={record?.lines[0].deliveredQtyTons}
										/>{' '}
										<T keyName="basicDetails.tons">t</T>
									</p>
								</SypacText>
							</>
						) : (
							'—'
						)}
					</div>
				);
			},
		};

		const price: ColumnType<Contract> = {
			title: t('contractsTable.contractPricePerTone', 'Contract price per ton'),
			dataIndex: '',
			className: 'text-left',
			key: 'contractPrice',
			width: 50,
			render(_value: any, record: Contract) {
				const sellerPricePerTon = Number(
					record?.lines?.[0]?.pricePerTonSeller ?? 0,
				);

				const markupPercent = Number(record?.lines?.[0]?.markup ?? 0);

				const buyerPricePerTon =
					record?.lines?.[0]?.pricePerTonBuyer * (1 + markupPercent / 100);

				if (role === 'admin') {
					return (
						<div className="flex flex-col gap-[10px]">
							<div className="h-[16px] flex gap-1 items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<T keyName="adminContracts.seller">Seller:</T>
									</p>
								</SypacText>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											decimalScale={2}
											fixedDecimalScale={false}
											value={numberTwoDecimal(sellerPricePerTon)}
											suffix=" PLN"
										/>
									</p>
								</SypacText>
							</div>

							<div className="h-[16px] flex gap-1 items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<T keyName="adminContracts.buyer">Buyer:</T>
									</p>
								</SypacText>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											decimalScale={2}
											fixedDecimalScale={false}
											value={numberTwoDecimal(buyerPricePerTon)}
											suffix=" PLN"
										/>
									</p>
								</SypacText>
							</div>
						</div>
					);
				}

				const valueToShow =
					role === 'producer' ? sellerPricePerTon : buyerPricePerTon;

				return (
					<SypacText variant="body-regular-medium">
						<p className="text-gray-80">
							<NumericFormat
								type="text"
								displayType="text"
								thousandSeparator=" "
								decimalSeparator="."
								decimalScale={2}
								fixedDecimalScale={false}
								value={valueToShow}
								suffix=" PLN"
							/>
						</p>
					</SypacText>
				);
			},
		};

		const dueCol: ColumnType<Contract> = {
			title: t('contractsTable.expirationDate', 'Expiration date'),
			dataIndex: '',
			className: 'text-left',
			key: 'expirationDate',
			render(_value: any, record: Contract) {
				return (
					<SypacText variant="body-regular-medium">
						<p className="text-gray-80">{formatDate(record?.expiresAt)}</p>
					</SypacText>
				);
			},
		};

		const ordersInProgress: ColumnType<Contract> = {
			title: t('contractsTable.inProgress', 'In progress'),
			dataIndex: '',
			key: 'ordersInProgress',
			align: 'center',
			className: 'text-center',
			render(_value: any, record: Contract) {
				return Number(record?.ordersInProgress) === 0 ? (
					'—'
				) : (
					<div className="inline-block w-[21px] h-[22px] leading-[22px] text-center border border-solid border-gray-10 rounded-[6px] bg-[#F4F4F4]">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 mt-[2px]">
								{record?.ordersInProgress}
							</p>
						</SypacText>
					</div>
				);
			},
		};

		const ordersDelivered: ColumnType<Contract> = {
			title: t('contractsTable.delivered', 'Delivered'),
			dataIndex: '',
			key: 'ordersDelivered',
			align: 'center',
			className: 'text-center',
			render(_value: any, record: Contract) {
				return Number(record?.ordersDelivered) === 0 ? (
					'—'
				) : (
					<div className="inline-block w-[21px] h-[22px] leading-[22px] text-center border border-solid border-gray-10 rounded-[6px] bg-[#F4F4F4]">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 mt-[2px]">{record?.ordersDelivered}</p>
						</SypacText>
					</div>
				);
			},
		};

		const cols: ColumnType<Contract>[] = [];

		if (role === 'admin') {
			cols.push(
				idCol,
				buyerCol,
				sellerCol,
				productCol,
				orderedQuantity,
				deliveredQuantity,
				price,
				dueCol,
				ordersInProgress,
				ordersDelivered,
			);
		} else {
			cols.push(idCol);

			if (role === 'buyer') {
				cols.push(sellerCol);
			} else if (role === 'producer') {
				cols.push(buyerCol);
			} else {
				cols.push(buyerCol, sellerCol);
			}

			cols.push(
				productCol,
				orderedQuantity,
				deliveredQuantity,
				price,
				dueCol,
				ordersInProgress,
				ordersDelivered,
			);
		}

		const enhanceClickable = (
			c: ColumnType<Contract>,
		): ColumnType<Contract> => ({
			...c,
			onCell: (record: Contract) => ({
				onClick: (e: React.MouseEvent) => {
					const a = (e.target as HTMLElement).closest('a');
					if (a) e.preventDefault();
					setSelectedRowKey(record.id);
					rowClick(record);
				},
			}),
		});

		return cols.map(enhanceClickable);
	}, [t, role, onCompanyClick, rowClick, search, language]);

	useEffect(() => {
		const newRows = rows.map((row: any) => ({ ...row, key: row?.id }));
		setRows(newRows);
	}, [rows]);

	const getTrProps = (record: Contract): string => {
		const baseClass = `contract-table-row-${record.id}`;
		const bgClass = ` contract-table-row-${record.status}`;
		const selectedId = selectedContractId ?? selectedRowKey;
		const isSelected =
			selectedId != null && record.id === Number(selectedId) && !clickedOutside;

		return `${baseClass}${bgClass}${
			isSelected ? ' contract-table-row-selected' : ''
		} cursor-pointer`;
	};

	return (
		<Table
			className={`contract-table ${
				keyRows.length === 0 ? 'h-full no-header' : 'h-full'
			}`}
			showHeader={keyRows.length > 0}
			columns={columns}
			data={keyRows}
			rowClassName={getTrProps}
			rowKey="id"
			onRow={(_record) => ({
				onMouseDown: (e: React.MouseEvent<HTMLTableRowElement>) => {
					e.stopPropagation();
				},
				onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
					const tr = e.currentTarget as HTMLTableRowElement;
					const contractId = tr.getAttribute('data-row-key') as
						| Contract['id']
						| null;
					if (!contractId) return;

					setSelectedRowKey(Number(contractId));
					const contract = rows.find((r: any) => r.id === Number(contractId));
					if (contract) {
						rowClick(contract);
					}
				},
			})}
			emptyText={<EmptyPlaceholder role={role} currentTab={currentTab} />}
		/>
	);
};
